/**
 * @file Home.js — Landing page publique de la plateforme communautaire Ringo.
 * Affiche une section hero, les fonctionnalités principales et un appel à l'action.
 * Accessible sans authentification. Adapte les boutons selon l'état de connexion.
 */
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Users, Calendar, Shield, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { APP_ROUTES } from '../lib/routes';

/**
 * Composant de la landing page.
 * @returns {React.ReactElement}
 */
export default function Home() {
  const { user } = useAuth();
  const { t } = useTranslation();

  const features = [
    { icon: Users, title: t('home.feature_circles_title'), desc: t('home.feature_circles_desc') },
    { icon: Calendar, title: t('home.feature_events_title'), desc: t('home.feature_events_desc') },
    { icon: Shield, title: t('home.feature_roles_title'), desc: t('home.feature_roles_desc') },
    { icon: Globe, title: t('home.feature_open_title'), desc: t('home.feature_open_desc') },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      {/* Hero */}
      <div className="flex flex-col items-center justify-center px-4 pt-24 pb-16">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {t('home.community_platform')}
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter text-foreground text-center" data-testid="home-heading" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('home.hero_title')}
        </h1>
        <p className="mt-4 text-base text-muted-foreground max-w-lg text-center leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          {t('home.hero_subtitle')}
        </p>
        <div className="mt-10 flex gap-3">
          {user ? (
            <Button asChild className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 gap-2" data-testid="go-to-dashboard-btn">
              <Link to={APP_ROUTES.dashboard}>{t('home.go_to_dashboard')} <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          ) : (
            <>
              <Button asChild className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="home-login-btn">
                <Link to={APP_ROUTES.login}>{t('home.sign_in')}</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-sm border-border hover:bg-muted" data-testid="home-register-btn">
                <Link to={APP_ROUTES.register}>{t('home.create_account')}</Link>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Features */}
      <div className="max-w-4xl mx-auto px-4 pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {features.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="border border-border rounded-sm p-6" data-testid="home-feature-card">
              <Icon className="h-5 w-5 text-foreground mb-3" />
              <h3 className="text-sm font-medium text-foreground mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border py-6 text-center space-y-2">
        <div className="flex justify-center gap-4 text-xs text-muted-foreground">
          <Link to={APP_ROUTES.privacy} className="hover:text-foreground hover:underline">{t('home.privacy')}</Link>
          <Link to={APP_ROUTES.cookies} className="hover:text-foreground hover:underline">{t('home.cookies')}</Link>
        </div>
        <p className="text-[10px] text-muted-foreground">&lt;Nom de l&apos;entreprise&gt; · TVA : &lt;Numéro de TVA&gt; · &lt;Adresse du siège social&gt;</p>
      </footer>
    </div>
  );
}
